/**
 * Meeting Transcription Workflow
 *
 * This workflow handles the complete process of:
 * 1. Transcribing a meeting recording with speaker diarization
 * 2. Allowing user to map speaker IDs to names
 * 3. Extracting action items from the transcript
 * 4. Creating tasks in the related project
 * 5. Storing the transcription for future reference
 */

import type { Workflow, SpeakerMapping, MeetingTranscription, ActionItem } from './types';

// =============================================================================
// WORKFLOW DEFINITION
// =============================================================================

export const meetingTranscriptionWorkflow: Workflow = {
  id: 'meeting-transcription',
  name: 'Trascrizione Riunione',
  description: 'Trascrive registrazione audio, identifica partecipanti, estrae task e aggiorna il progetto',
  trigger: {
    type: 'audio_upload',
    config: {
      // Only trigger for audio files
      mimeTypes: ['audio/mpeg', 'audio/wav', 'audio/webm', 'audio/m4a', 'audio/mp4'],
      // Optional: only for files with "meeting" or "riunione" in name
      namePatterns: ['meeting', 'riunione', 'call'],
    },
  },
  steps: [
    {
      id: 'transcribe',
      name: 'Trascrivi Audio',
      type: 'transcribe_diarize',
      config: {
        language: 'it',
        minSpeakers: 2,
        maxSpeakers: 10,
      },
      next: ['identify_speakers'],
    },
    {
      id: 'identify_speakers',
      name: 'Identifica Partecipanti',
      type: 'custom',
      config: {
        // This step pauses and asks user to map speaker IDs to names
        requireUserInput: true,
        prompt: 'Chi sono i partecipanti? Assegna i nomi agli speaker identificati.',
      },
      next: ['extract_tasks'],
    },
    {
      id: 'extract_tasks',
      name: 'Estrai Task',
      type: 'ai_analyze',
      config: {
        prompt: `Analizza questa trascrizione di riunione e estrai tutti i task/azioni da fare.
Per ogni task identifica:
- Descrizione dell'azione
- Chi deve farla (se menzionato)
- Eventuale scadenza menzionata
- Priorità (alta/media/bassa) basata sul contesto

Rispondi in formato JSON:
{
  "tasks": [
    {
      "description": "...",
      "assignee": "nome persona o null",
      "dueDate": "data se menzionata o null",
      "priority": "high|medium|low"
    }
  ]
}`,
      },
      next: ['create_tasks'],
    },
    {
      id: 'create_tasks',
      name: 'Crea Task nel Progetto',
      type: 'create_tasks',
      config: {
        // Use project from context or ask user
        projectIdField: 'selectedProjectId',
        tasksField: 'extractedTasks',
      },
      next: ['store_transcription'],
    },
    {
      id: 'store_transcription',
      name: 'Salva Trascrizione',
      type: 'custom',
      config: {
        // Store the full transcription as a document
        storeInProject: true,
        generateSummary: true,
      },
      next: ['notify_complete'],
    },
    {
      id: 'notify_complete',
      name: 'Notifica Completamento',
      type: 'send_notification',
      config: {
        type: 'in_app',
        message: 'Trascrizione completata! Ho creato {taskCount} task dal meeting.',
      },
    },
  ],
  enabled: true,
  version: 1,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Parse AI response to extract action items
 */
export function parseActionItems(aiResponse: string): ActionItem[] {
  try {
    // Try to extract JSON from response
    const jsonMatch = aiResponse.match(/\{[\s\S]*\}/);
    if (!jsonMatch) return [];

    const parsed = JSON.parse(jsonMatch[0]);
    if (!parsed.tasks || !Array.isArray(parsed.tasks)) return [];

    return parsed.tasks.map((t: any) => ({
      description: t.description || '',
      assignee: t.assignee || undefined,
      dueDate: t.dueDate || undefined,
      priority: t.priority || 'medium',
    }));
  } catch (e) {
    console.error('Failed to parse action items:', e);
    return [];
  }
}

/**
 * Apply speaker mappings to transcript segments
 */
export function applySpelabelMapping(
  transcription: MeetingTranscription,
  mappings: SpeakerMapping[]
): MeetingTranscription {
  const speakerMap = new Map(mappings.map(m => [m.speakerId, m]));

  return {
    ...transcription,
    speakers: mappings,
    segments: transcription.segments.map(seg => ({
      ...seg,
      speakerName: speakerMap.get(seg.speaker)?.name || seg.speaker,
    })),
    transcript: formatTranscriptWithNames(transcription.segments, speakerMap),
  };
}

/**
 * Format transcript text with speaker names
 */
function formatTranscriptWithNames(
  segments: MeetingTranscription['segments'],
  speakerMap: Map<string, SpeakerMapping>
): string {
  const lines: string[] = [];
  let currentSpeaker = '';

  for (const seg of segments) {
    const speakerName = speakerMap.get(seg.speaker)?.name || seg.speaker;
    if (speakerName !== currentSpeaker) {
      lines.push(`\n[${speakerName}]: ${seg.text}`);
      currentSpeaker = speakerName;
    } else {
      lines.push(seg.text);
    }
  }

  return lines.join(' ').trim();
}

/**
 * Generate a meeting summary using AI
 */
export async function generateMeetingSummary(
  transcript: string,
  aiAnalyze: (prompt: string) => Promise<string>
): Promise<string> {
  const prompt = `Riassumi questa riunione in modo conciso (max 200 parole):

${transcript}

Il riassunto deve includere:
- Argomenti principali discussi
- Decisioni prese
- Prossimi passi`;

  return aiAnalyze(prompt);
}
